/**
 * FeeAssumptionsInput Component
 * Phase 7 Batch 7.3.3 - Fee Assumptions Library
 *
 * Input component for fee assumptions with:
 * - Searchable combobox to quick-add from library
 * - Plain textarea with CSS line separators for custom assumptions
 * - Help text explaining "each line becomes a numbered item"
 *
 * Used in ProjectForm and ProjectDetailPage for inline editing.
 */
import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useLibraryAssumptions } from '@/hooks/useFeeAssumptions';

interface FeeAssumptionsInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  disabled?: boolean;
}

export function FeeAssumptionsInput({
  value,
  onChange,
  label = 'Fee Assumptions',
  className = '',
  disabled = false,
}: FeeAssumptionsInputProps) {
  const [open, setOpen] = React.useState(false);
  const { data, isLoading } = useLibraryAssumptions();
  const library = data?.fee_assumptions || [];

  // Handle quick-add from library
  const handleSelectAssumption = (assumptionText: string) => {
    // Append to textarea with newline
    const newValue = value ? `${value}\n${assumptionText}` : assumptionText;
    onChange(newValue);
    // Keep popover open so user can add multiple assumptions without re-clicking
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Header with label */}
      <div>
        <Label htmlFor="fee-assumptions">{label}</Label>
        <p className="text-xs text-muted-foreground mt-1">
          Press{' '}
          <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded text-xs font-mono">
            Enter
          </kbd>{' '}
          to create a new numbered item
        </p>
      </div>

      {/* Quick Add Combobox */}
      <div>
        <Label className="text-xs text-muted-foreground">Quick Add from Library</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={disabled || library.length === 0}
            >
              {library.length === 0
                ? 'No library assumptions'
                : 'Search library assumptions...'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" align="start">
            <Command>
              <CommandInput placeholder="Search library..." />
              <CommandList>
                <CommandEmpty>
                  {isLoading ? 'Loading...' : 'No assumptions found'}
                </CommandEmpty>
                <CommandGroup>
                  {library.map((assumption) => (
                    <CommandItem
                      key={assumption.id}
                      value={assumption.assumption_text}
                      onSelect={() => handleSelectAssumption(assumption.assumption_text)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          // Show check if this assumption is already in the textarea
                          value?.includes(assumption.assumption_text)
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {assumption.assumption_text}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Textarea with CSS line separators */}
      <div>
        <Textarea
          id="fee-assumptions"
          rows={6}
          placeholder="Based on 500 sq ft addition&#10;Standard residential construction rates&#10;Typical architectural services phases"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="leading-7 resize-none"
          style={{
            backgroundImage:
              'repeating-linear-gradient(transparent, transparent 1.75rem, rgba(148, 163, 184, 0.2) 1.75rem, rgba(148, 163, 184, 0.2) calc(1.75rem + 1px))',
            backgroundAttachment: 'local',
            lineHeight: '1.75rem',
            paddingTop: '0.5rem',
          }}
        />
      </div>
    </div>
  );
}
